import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import AccountMenu from 'components/account/AccountMenu';
import { LoginSection } from 'components/landing-pages-v2/shared/header/LoginSection';
import { useObservability } from 'helpers/observability';
import { useBuyerHubFlagStatus } from 'helpers/useBuyerHubFlag';
import { usePrefetchNavTransactions } from 'helpers/useNavTransactions';

import { GlobalNavSVG } from './GlobalNavSVG';
import { useLargerThanSM } from './useLargerThanSM';

interface AccountSectionProps {
  isLoggedIn: boolean;
  showLogin: boolean;
  initials: string[];
  redirectToLogin: () => void;
  trackAccountClick: () => void;
  trackMenuClick: () => void;
  onMobileMenuToggle: () => void;
  isMobileMenuVisible: boolean;
  hamburgerRef?: React.RefObject<HTMLButtonElement>;
  // When true, the menu/dots/sign-in render in the inverse (light) foreground for
  // use over a dark/transparent header; otherwise they use the primary foreground.
  inverse?: boolean;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
  isLoggedIn,
  showLogin,
  initials,
  redirectToLogin,
  trackAccountClick,
  trackMenuClick,
  onMobileMenuToggle,
  isMobileMenuVisible,
  hamburgerRef,
  inverse,
}) => {
  const foregroundClass = inverse ? 'text-od-content-inverse-primary' : 'text-od-content-primary';
  const { trackJourneyEvent } = useObservability();
  const largerThanSM = useLargerThanSM();
  const [isExpanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Warm the shared nav-transactions store from the always-mounted header so the
  // mobile menu (whose list only mounts on open) is populated instantly. Only
  // prefetch for users in the buyer-hub rollout — the redesigned menu that reads
  // it is gated behind the same flag.
  const hubNavEnabled = useBuyerHubFlagStatus() === 'enabled';
  usePrefetchNavTransactions(hubNavEnabled);

  const toggleDropdown = useCallback(() => setExpanded((prev) => !prev), []);
  const closeDropdown = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, closeDropdown]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  useEffect(() => {
    if (!isExpanded) return;

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, handleEscape]);

  const handleAvatarPress = useCallback(() => {
    trackAccountClick();
    trackMenuClick();
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'user-account-menu-click' });
    toggleDropdown();
  }, [trackAccountClick, trackMenuClick, trackJourneyEvent, toggleDropdown]);

  const handleLoginPress = useCallback(() => {
    trackAccountClick();
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'account-section-click' });
    redirectToLogin();
  }, [trackAccountClick, trackJourneyEvent, redirectToLogin]);

  const handleDotsPress = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      trackMenuClick();
      toggleDropdown();
    },
    [trackMenuClick, toggleDropdown],
  );

  const handleMobileMenuPress = useCallback(() => {
    trackMenuClick();
    onMobileMenuToggle();
  }, [trackMenuClick, onMobileMenuToggle]);

  const hamburger = (
    <Box className="md:hidden">
      <Box
        ref={hamburgerRef}
        onClick={handleMobileMenuPress}
        aria-label="Navigation menu"
        aria-haspopup="true"
        aria-expanded={isMobileMenuVisible}
        analyticsName="cosmos-landing-page-header-menu-button"
        className={cn(
          'flex cursor-pointer items-center justify-center focus:outline-none',
          foregroundClass,
        )}
      >
        <GlobalNavSVG isOpen={isMobileMenuVisible} />
      </Box>
    </Box>
  );

  return (
    <Box ref={containerRef} className="relative flex items-center gap-3">
      {isLoggedIn ? (
        <>
          <Box
            onClick={handleAvatarPress}
            analyticsName="account-section-click"
            aria-label="User account menu"
            aria-haspopup="true"
            aria-expanded={isExpanded}
            className="cursor-pointer border-0 bg-transparent p-0 max-md:hidden"
          >
            <Box className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-od-bg-inverse-primary">
              <Text as="span" className="text-label-sm font-medium text-od-content-inverse-primary">
                {initials}
              </Text>
            </Box>
          </Box>
          {hamburger}
        </>
      ) : (
        <Row className="items-center gap-3">
          <LoginSection showLogin={showLogin} onPress={handleLoginPress} inverse={inverse} />
          <Box className="max-md:hidden">
            <Box
              onClick={handleDotsPress}
              aria-label="More options"
              aria-haspopup="true"
              aria-expanded={isExpanded}
              analyticsName="cosmos-landing-page-header-menu-button"
              className={cn('flex cursor-pointer items-center justify-center', foregroundClass)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
                data-testid="icon-dots"
              >
                <circle cx="4" cy="10" r="1.75" />
                <circle cx="10" cy="10" r="1.75" />
                <circle cx="16" cy="10" r="1.75" />
              </svg>
            </Box>
          </Box>
          {hamburger}
        </Row>
      )}
      {largerThanSM && <AccountMenu isExpanded={isExpanded} isLoggedIn={isLoggedIn} />}
    </Box>
  );
};
